import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';

// Home's daily completeness checklist (PH5). Ticking a domain "complete" is an
// attestation that the day's logging is done — which lifts that day from
// presence-only to a day whose manual data can actually sharpen your guidance.
// It doubles as a memory aid, and it reopens for the after-dinner snack.

const todayStr = () => new Date().toISOString().split('T')[0];

const fmtTime = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return null;
  }
};

const DomainRow = ({ d, onToggleItem, onSetComplete, saving }) => (
  <div className="rounded-xl border border-surface-border dark:border-surface-dark-border p-4">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 mb-3">
      <div className="min-w-0">
        <p className="text-base font-semibold text-gray-900 dark:text-white">{d.label}</p>
        {d.completed ? (
          <p className="text-sm text-good-strong dark:text-good">
            Logged in full{fmtTime(d.completed_at) ? ` · done at ${fmtTime(d.completed_at)}` : ''}
          </p>
        ) : (
          <p className="text-sm text-gray-600 dark:text-gray-300">Presence-only until you confirm</p>
        )}
      </div>
      {d.completed ? (
        <button
          type="button"
          onClick={() => onSetComplete(d.domain, false)}
          disabled={saving}
          className="self-start sm:self-auto sm:shrink-0 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white disabled:opacity-50"
        >
          Reopen
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onSetComplete(d.domain, true)}
          disabled={saving}
          className="w-full sm:w-auto sm:shrink-0 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
        >
          Mark complete
        </button>
      )}
    </div>
    <div className="flex flex-wrap gap-2">
      {d.checklist.map((item) => {
        const ticked = (d.items || []).includes(item);
        return (
          <button
            key={item}
            type="button"
            onClick={() => onToggleItem(d.domain, item)}
            disabled={d.completed || saving}
            className={`text-sm px-2.5 py-1 rounded-full border transition-colors disabled:opacity-70 ${
              ticked
                ? 'bg-good/15 border-good/40 text-good-strong dark:text-good'
                : 'border-surface-border dark:border-surface-dark-border text-gray-600 dark:text-gray-300'
            }`}
          >
            {ticked ? '✓ ' : ''}{item}
          </button>
        );
      })}
    </div>
  </div>
);

const CompletenessCard = () => {
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const day = todayStr();

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/completeness', { params: { target_date: day } });
      setStatus(res.data);
    } catch {
      // Silent: the card just doesn't render if the fetch fails.
    }
  }, [day]);

  useEffect(() => { load(); }, [load]);

  const toggleItem = (domain, item) => {
    setStatus((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        domains: prev.domains.map((d) => {
          if (d.domain !== domain) return d;
          const items = d.items || [];
          return {
            ...d,
            items: items.includes(item) ? items.filter((i) => i !== item) : [...items, item],
          };
        }),
      };
    });
  };

  const setComplete = async (domain, completed) => {
    const d = status?.domains.find((x) => x.domain === domain);
    setSaving(true);
    try {
      const res = await apiClient.put('/api/completeness', {
        domain,
        completed,
        items: d?.items || [],
        date: day,
      });
      setStatus(res.data);
    } catch {
      toast.error('Could not save that just now');
    } finally {
      setSaving(false);
    }
  };

  if (!status) return null;

  return (
    <div className="rounded-2xl border border-surface-border dark:border-surface-dark-border bg-surface-card dark:bg-surface-dark-card p-5">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Did you log it all?</h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Confirming a day is complete lets its food &amp; exercise sharpen your guidance —
          incomplete days still count for timing and habits, just not the numbers.
        </p>
      </div>
      <div className="space-y-3">
        {status.domains.map((d) => (
          <DomainRow
            key={d.domain}
            d={d}
            onToggleItem={toggleItem}
            onSetComplete={setComplete}
            saving={saving}
          />
        ))}
      </div>
    </div>
  );
};

export default CompletenessCard;
